import axios from "@/plugins/axios";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const INITIAL_STALE_MS = 45 * 1000;
const CACHE_MAX_AGE_MS = 3 * 60 * 1000;
const CACHE_KEY = "dashboard_insights_cache";
const AI_CACHE_KEY = "dashboard_insights_ai_cache";

function normalizeAiPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.ai && typeof payload.ai === "object") return payload.ai;
  return payload;
}

function normalizeMetricsPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.metrics && typeof payload.metrics === "object") return payload;
  return payload;
}

function writeDashboardCache(data) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        data,
        fetchedAt: Date.now(),
      })
    );
  } catch (_) {
    // ignore storage failures
  }
}

function readDashboardCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.data || !parsed.fetchedAt) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function clearDashboardCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (_) {
    // ignore storage failures
  }
}

function writeDashboardAiCache(data) {
  try {
    localStorage.setItem(
      AI_CACHE_KEY,
      JSON.stringify({
        data,
        fetchedAt: Date.now(),
      })
    );
  } catch (_) {
    // ignore storage failures
  }
}

function readDashboardAiCache() {
  try {
    const raw = localStorage.getItem(AI_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.fetchedAt) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function clearDashboardAiCache() {
  try {
    localStorage.removeItem(AI_CACHE_KEY);
  } catch (_) {
    // ignore storage failures
  }
}

export const state = {
  data: null,
  aiData: null,
  loading: false,
  refreshing: false,
  aiLoading: false,
  aiRefreshing: false,
  error: null,
  aiError: null,
  lastFetchedAt: null,
  aiLastFetchedAt: null,
  refreshIntervalId: null,
};

export const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_REFRESHING(state, refreshing) {
    state.refreshing = refreshing;
  },
  SET_DATA(state, data) {
    state.data = data;
    state.lastFetchedAt = Date.now();
  },
  SET_AI_DATA(state, aiData) {
    state.aiData = aiData;
    state.aiLastFetchedAt = Date.now();
  },
  SET_DATA_WITH_TIMESTAMP(state, payload = {}) {
    state.data = payload.data || null;
    state.lastFetchedAt = payload.lastFetchedAt || null;
  },
  SET_AI_DATA_WITH_TIMESTAMP(state, payload = {}) {
    state.aiData = payload.data || null;
    state.aiLastFetchedAt = payload.lastFetchedAt || null;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_AI_ERROR(state, error) {
    state.aiError = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  CLEAR_AI_ERROR(state) {
    state.aiError = null;
  },
  SET_AI_LOADING(state, loading) {
    state.aiLoading = loading;
  },
  SET_AI_REFRESHING(state, refreshing) {
    state.aiRefreshing = refreshing;
  },
  SET_REFRESH_INTERVAL_ID(state, intervalId) {
    state.refreshIntervalId = intervalId;
  },
  CLEAR_REFRESH_INTERVAL(state) {
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
    }
    state.refreshIntervalId = null;
  },
  RESET(state) {
    if (state.refreshIntervalId) {
      clearInterval(state.refreshIntervalId);
    }
    state.data = null;
    state.aiData = null;
    state.loading = false;
    state.refreshing = false;
    state.aiLoading = false;
    state.aiRefreshing = false;
    state.error = null;
    state.aiError = null;
    state.lastFetchedAt = null;
    state.aiLastFetchedAt = null;
    state.refreshIntervalId = null;
  },
};

export const getters = {
  data: (state) => state.data,
  metrics: (state) => (state.data && state.data.metrics) || null,
  ai: (state) => state.aiData,
  loading: (state) => state.loading,
  refreshing: (state) => state.refreshing,
  aiLoading: (state) => state.aiLoading,
  aiRefreshing: (state) => state.aiRefreshing,
  error: (state) => state.error,
  aiError: (state) => state.aiError,
  lastFetchedAt: (state) => state.lastFetchedAt,
  aiLastFetchedAt: (state) => state.aiLastFetchedAt,
  hasData: (state) => !!state.data,
  hasAiData: (state) => !!state.aiData,
  isStale: (state) => {
    if (!state.lastFetchedAt) return true;
    return Date.now() - state.lastFetchedAt >= REFRESH_INTERVAL_MS;
  },
  isAiStale: (state) => {
    if (!state.aiLastFetchedAt) return true;
    return Date.now() - state.aiLastFetchedAt >= REFRESH_INTERVAL_MS;
  },
  isInitialStale: (state) => {
    if (!state.lastFetchedAt) return true;
    return Date.now() - state.lastFetchedAt >= INITIAL_STALE_MS;
  },
  isInitialAiStale: (state) => {
    if (!state.aiLastFetchedAt) return true;
    return Date.now() - state.aiLastFetchedAt >= INITIAL_STALE_MS;
  },
};

export const actions = {
  restoreFromCache({ commit }) {
    const cached = readDashboardCache();
    if (!cached || !cached.data || !cached.fetchedAt) {
      return { restored: false, tooOld: true, ageMs: null };
    }

    const ageMs = Date.now() - Number(cached.fetchedAt || 0);
    if (!Number.isFinite(ageMs) || ageMs < 0 || ageMs >= CACHE_MAX_AGE_MS) {
      clearDashboardCache();
      return { restored: false, tooOld: true, ageMs: null };
    }

    commit("SET_DATA_WITH_TIMESTAMP", {
      data: normalizeMetricsPayload(cached.data),
      lastFetchedAt: Number(cached.fetchedAt),
    });

    return { restored: true, tooOld: false, ageMs };
  },

  restoreAiFromCache({ commit }) {
    const cached = readDashboardAiCache();
    if (!cached || !cached.fetchedAt) {
      return { restored: false, tooOld: true, ageMs: null };
    }

    const ageMs = Date.now() - Number(cached.fetchedAt || 0);
    if (!Number.isFinite(ageMs) || ageMs < 0 || ageMs >= CACHE_MAX_AGE_MS) {
      clearDashboardAiCache();
      return { restored: false, tooOld: true, ageMs: null };
    }

    commit("SET_AI_DATA_WITH_TIMESTAMP", {
      data: normalizeAiPayload(cached.data) || null,
      lastFetchedAt: Number(cached.fetchedAt),
    });

    return { restored: true, tooOld: false, ageMs };
  },

  async fetchInsights({ state, commit, getters }, options = {}) {
    const background = !!options.background;
    const force = !!options.force;
    const hasData = !!state.data;

    if (state.refreshing) {
      return state.data;
    }

    if (!force && hasData && !getters.isStale) {
      return state.data;
    }

    if (!background && !hasData) {
      commit("SET_LOADING", true);
    }

    commit("SET_REFRESHING", true);
    commit("CLEAR_ERROR");

    try {
      const res = await axios.get("/dashboard/insights");
      const rawPayload = res.data && res.data.data ? res.data.data : res.data;
      const payload = normalizeMetricsPayload(rawPayload);
      commit("SET_DATA", payload);
      writeDashboardCache(payload);
      return payload;
    } catch (error) {
      commit("SET_ERROR", error);
      return state.data;
    } finally {
      commit("SET_LOADING", false);
      commit("SET_REFRESHING", false);
    }
  },

  async fetchInsightsAi({ state, commit, getters }, options = {}) {
    const background = !!options.background;
    const force = !!options.force;
    const hasData = !!state.aiData;

    if (state.aiRefreshing) {
      return state.aiData;
    }

    if (!force && hasData && !getters.isAiStale) {
      return state.aiData;
    }

    if (!background && !hasData) {
      commit("SET_AI_LOADING", true);
    }

    commit("SET_AI_REFRESHING", true);
    commit("CLEAR_AI_ERROR");

    try {
      const res = await axios.get("/dashboard/insights/ai");
      const rawPayload = res.data && res.data.data ? res.data.data : res.data;
      const payload = normalizeAiPayload(rawPayload);
      commit("SET_AI_DATA", payload);
      writeDashboardAiCache(payload);
      return payload;
    } catch (error) {
      commit("SET_AI_ERROR", error);
      return state.aiData;
    } finally {
      commit("SET_AI_LOADING", false);
      commit("SET_AI_REFRESHING", false);
    }
  },

  fetchAllInsights({ dispatch }, options = {}) {
    const includeAi = options.includeAi !== false;
    const requests = [dispatch("fetchInsights", options)];

    if (includeAi) {
      requests.push(dispatch("fetchInsightsAi", options));
    }

    return Promise.all(requests);
  },

  ensureAutoRefresh({ state, commit, dispatch }, options = {}) {
    if (state.refreshIntervalId) {
      return;
    }

    const includeAi = options.includeAi !== false;
    const intervalId = setInterval(() => {
      dispatch("fetchAllInsights", {
        background: true,
        force: true,
        includeAi,
      });
    }, REFRESH_INTERVAL_MS);

    commit("SET_REFRESH_INTERVAL_ID", intervalId);
  },

  clearAutoRefresh({ commit }) {
    commit("CLEAR_REFRESH_INTERVAL");
  },

  reset({ commit }) {
    clearDashboardCache();
    clearDashboardAiCache();
    commit("RESET");
  },
};
